import { ref, computed } from "vue";
import { supabase } from "../db/supabaseClient.js";
import { formatMarkdownToHtml } from "../utils/analyticsUtils.js";

const mistralUrl =
  import.meta.env.VITE_MISTRAL_API_URL || import.meta.env.MISTRAL_API_URL;
const mistralKey =
  import.meta.env.VITE_MISTRAL_API_KEY || import.meta.env.MISTRAL_API_KEY;
const defaultModel =
  import.meta.env.VITE_MISTRAL_MODEL ||
  import.meta.env.MISTRAL_MODEL ||
  "mistral-small-latest";

export function useAnalyticsAI() {
  const forecastDaily = ref([]);
  const forecastMonthly = ref([]);
  const forecastYearly = ref([]);
  
  const maxForecastDaily = computed(() =>
    Math.max(1, ...forecastDaily.value.map((d) => Number(d.amount || 0)))
  );
  const maxForecastMonthly = computed(() =>
    Math.max(1, ...forecastMonthly.value.map((d) => Number(d.amount || 0)))
  );
  const maxForecastYearly = computed(() =>
    Math.max(1, ...forecastYearly.value.map((d) => Number(d.amount || 0)))
  );

  const aiText = ref("");
  const aiLoading = ref(false);
  const aiHtml = computed(() => formatMarkdownToHtml(aiText.value || ""));

  async function generateForecast(salesDaily, salesMonthly, salesYearly) {
    try {
      // Usar variables directamente para asegurar que funcionen en producción
      let baseUrl = import.meta.env.VITE_MISTRAL_API_URL || import.meta.env.MISTRAL_API_URL;
      const key = import.meta.env.VITE_MISTRAL_API_KEY || import.meta.env.MISTRAL_API_KEY;
      const model = import.meta.env.VITE_MISTRAL_MODEL || import.meta.env.MISTRAL_MODEL || "mistral-small-latest";
      
      if (!baseUrl || !key) {
        console.warn('[IA] Variables de Mistral no configuradas');
        return;
      }
      
      // Asegurar que la URL incluya el path completo de la API
      // Si solo tiene la base URL, agregar el path
      if (!baseUrl.includes('/v1/chat/completions')) {
        baseUrl = baseUrl.replace(/\/$/, '') + '/v1/chat/completions';
      }
      
      const url = baseUrl;
      aiLoading.value = true;
      const dailyHist = salesDaily.slice(-60);
      const monthlyHist = salesMonthly.slice(-12);
      const yearlyHist = salesYearly.slice(-5);
      const prompt = `Genera pronósticos en JSON con las llaves daily_forecast, monthly_forecast, yearly_forecast. daily_forecast: próximos 7 días {date,amount}. monthly_forecast: próximos 6 meses {month,amount}. yearly_forecast: próximos 2 años {year,amount}. Usa MXN. Históricos: daily=${JSON.stringify(
        dailyHist
      )}, monthly=${JSON.stringify(monthlyHist)}, yearly=${JSON.stringify(
        yearlyHist
      )}.`;
      const payload = {
        model: model,
        messages: [
          {
            role: "system",
            content: "Eres un analista que devuelve sólo JSON válido.",
          },
          { role: "user", content: prompt },
        ],
        temperature: 0.2,
        max_tokens: 600,
      };
      console.log('[IA] Enviando request a:', url);
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('[IA] Error en respuesta:', res.status, errorData);
        throw new Error(errorData?.error?.message || `Error ${res.status}`);
      }
      const data = await res.json();
      let txt = (data?.choices?.[0]?.message?.content || "").trim();
      let jsonStr = txt;
      const m = txt.match(/\{[\s\S]*\}/);
      if (m) jsonStr = m[0];
      const obj = JSON.parse(jsonStr);
      forecastDaily.value = Array.isArray(obj.daily_forecast)
        ? obj.daily_forecast.map((d) => ({
            date: d.date,
            amount: Number(d.amount || 0),
          }))
        : [];
      forecastMonthly.value = Array.isArray(obj.monthly_forecast)
        ? obj.monthly_forecast.map((d) => ({
            month: d.month,
            amount: Number(d.amount || 0),
          }))
        : [];
      forecastYearly.value = Array.isArray(obj.yearly_forecast)
        ? obj.yearly_forecast.map((d) => ({
            year: Number(d.year || 0),
            amount: Number(d.amount || 0),
          }))
        : [];
    } catch (err) {
      console.error('[IA] Error generando pronóstico:', err);
      forecastDaily.value = [];
      forecastMonthly.value = [];
      forecastYearly.value = [];
    } finally {
      aiLoading.value = false;
    }
  }

  async function generateAIInsights(kpis, salesDetails, purchasesDetails, cashFlowDays, activeRange) {
    try {
      // Usar variables directamente para asegurar que funcionen en producción
      let baseUrl = import.meta.env.VITE_MISTRAL_API_URL || import.meta.env.MISTRAL_API_URL;
      const key = import.meta.env.VITE_MISTRAL_API_KEY || import.meta.env.MISTRAL_API_KEY;
      const model = import.meta.env.VITE_MISTRAL_MODEL || import.meta.env.MISTRAL_MODEL || "mistral-small-latest";
      
      if (!baseUrl || !key) {
        console.warn('[IA] Variables de Mistral no configuradas');
        return;
      }
      
      // Asegurar que la URL incluya el path completo de la API
      // Si solo tiene la base URL, agregar el path
      if (!baseUrl.includes('/v1/chat/completions')) {
        baseUrl = baseUrl.replace(/\/$/, '') + '/v1/chat/completions';
      }
      
      const url = baseUrl;
      aiLoading.value = true;
      const sum = {
        ventas: kpis.ventas,
        ingresos: kpis.ingresos,
        impuestos: kpis.impuestos,
        compras: kpis.compras,
        comprasImporte: kpis.comprasImporte,
        balance:
          Number(kpis.ingresos || 0) -
          Number(kpis.comprasImporte || 0),
      };
      let topSales = salesDetails.slice().reduce((m, r) => {
        const k = String(r.product || "");
        m.set(k, (m.get(k) || 0) + Number(r.total || 0));
        return m;
      }, new Map());
      let topPurch = purchasesDetails.slice().reduce((m, r) => {
        const k = String(r.product || "");
        m.set(k, (m.get(k) || 0) + Number(r.total || 0));
        return m;
      }, new Map());
      
      if (topSales.size === 0 || topPurch.size === 0) {
        const r = activeRange;
        if (topSales.size === 0) {
          const { data: vs } = await supabase
            .from("v_base_sales")
            .select("product,total,date")
            .gte("date", r.start)
            .lte("date", r.end);
          topSales = (vs || []).reduce((m, row) => {
            const k = String(row.product || "");
            m.set(k, (m.get(k) || 0) + Number(row.total || 0));
            return m;
          }, new Map());
        }
        if (topPurch.size === 0) {
          const { data: vp } = await supabase
            .from("v_base_purchases")
            .select("product,total,date")
            .gte("date", r.start)
            .lte("date", r.end);
          topPurch = (vp || []).reduce((m, row) => {
            const k = String(row.product || "");
            m.set(k, (m.get(k) || 0) + Number(row.total || 0));
            return m;
          }, new Map());
        }
      }
      const topSalesList = Array.from(topSales.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      const topPurchList = Array.from(topPurch.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      const flow = cashFlowDays
        .slice(-14)
        .map((d) => ({ date: d.date, income: d.income, expense: d.expense }));
      const prompt = `Eres un analista senior. Analiza métricas y tendencias y genera insights accionables en español, con bullets claros y recomendaciones. Datos: sum=${JSON.stringify(
        sum
      )}, topVentas=${JSON.stringify(topSalesList)}, topCompras=${JSON.stringify(
        topPurchList
      )}, flujo=${JSON.stringify(flow)}.`;
      const payload = {
        model: model,
        messages: [
          {
            role: "system",
            content:
              "Eres un analista de datos experto. Responde en español con insights prácticos y breves.",
          },
          { role: "user", content: prompt },
        ],
        temperature: 0.2,
        max_tokens: 400,
      };
      console.log('[IA] Enviando request a:', url);
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('[IA] Error en respuesta:', res.status, errorData);
        throw new Error(errorData?.error?.message || `Error ${res.status}`);
      }
      const data = await res.json();
      aiText.value = (data?.choices?.[0]?.message?.content || "").trim();
    } catch (err) {
      console.error('[IA] Error generando insights:', err);
      aiText.value = `Error en el análisis: ${err?.message || 'Error desconocido'}`;
    } finally {
      aiLoading.value = false;
    }
  }

  return {
    forecastDaily,
    forecastMonthly,
    forecastYearly,
    maxForecastDaily,
    maxForecastMonthly,
    maxForecastYearly,
    aiText,
    aiLoading,
    aiHtml,
    generateForecast,
    generateAIInsights,
  };
}
